/* Ciftora Piyasa — paylaşılan arayüz parçaları: il seçici, kaynak rozeti, fiyat kartı. */
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Check, ExternalLink, Search, Star, X } from "lucide-react";
import { cachedGet } from "@/lib/api";

export const TIER_STYLE = {
  A: "bg-primary text-primary-foreground",
  B: "bg-secondary text-secondary-foreground",
  C: "bg-accent text-accent-foreground",
};

export const nf = (v, digits = 2) =>
  v == null ? "-" : new Intl.NumberFormat("tr-TR", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);

export const dayTR = (v) =>
  v ? new Date(v).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" }) : null;

export const minuteTR = (v) =>
  v ? new Date(v).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null;

export const FREQ_LABEL = {
  REALTIME: "İşlem oldukça güncellenir",
  HOURLY: "Saatlik",
  DAILY: "Günlük",
  WEEKLY: "Haftalık",
  MONTHLY: "Aylık",
  IRREGULAR: "Kaynak açıkladıkça",
};

export const SourceBadge = ({ tier, badge, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${TIER_STYLE[tier] || "bg-muted text-primary"}`}
  >
    {badge}
  </span>
);

export function useProvinces() {
  const [provinces, setProvinces] = useState([]);
  useEffect(() => {
    cachedGet("/public/market/provinces").then((d) => setProvinces(d.provinces || [])).catch(() => {});
  }, []);
  return provinces;
}

/** 81 il için aramalı seçici. Favori iller (en çok 5) üstte gösterilir. */
export function ProvincePicker({ value, onSelect, favorites = [], onToggleFavorite, testid = "province-picker" }) {
  const provinces = useProvinces();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onClick = (e) => !boxRef.current?.contains(e.target) && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr");
    if (!q) return provinces;
    return provinces.filter((p) => p.name.toLocaleLowerCase("tr").includes(q) || p.slug.includes(q));
  }, [provinces, query]);

  return (
    <div className="relative" data-testid={testid} ref={boxRef}>
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className="flex h-12 w-full items-center justify-between gap-2 rounded-lg border border-border bg-card px-4 text-sm font-bold text-primary hover:bg-muted sm:w-72"
      >
        <span>{value || "İl seçin"}</span>
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div
          data-testid={`${testid}-panel`}
          className="absolute z-40 mt-2 w-full rounded-lg border border-border bg-card p-3 shadow-md sm:w-72"
        >
          <div className="flex items-center gap-2">
            <input
              autoFocus
              data-testid={`${testid}-search`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="İl ara"
              className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary"
            />
            <button type="button" data-testid={`${testid}-close`} onClick={() => setOpen(false)} className="p-2 text-muted-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>

          {favorites.length > 0 && !query && (
            <div className="mt-3">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Favori illerim</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {favorites.map((f) => (
                  <button
                    key={f}
                    type="button"
                    data-testid={`${testid}-fav-${f}`}
                    onClick={() => { onSelect(f); setOpen(false); }}
                    className="rounded-full border border-border px-3 py-1 text-xs font-bold text-primary hover:bg-muted"
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
          )}

          <ul className="mt-3 max-h-72 space-y-0.5 overflow-y-auto">
            {filtered.map((p) => (
              <li key={p.slug} className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid={`${testid}-option-${p.slug}`}
                  onClick={() => { onSelect(p.name); setOpen(false); setQuery(""); }}
                  className={`flex flex-1 items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm hover:bg-muted ${
                    value === p.name ? "font-bold text-primary" : "text-foreground"
                  }`}
                >
                  <span>{p.name}</span>
                  <span className="text-[11px] text-muted-foreground">{p.region}</span>
                </button>
                {onToggleFavorite && (
                  <button
                    type="button"
                    data-testid={`${testid}-favtoggle-${p.slug}`}
                    onClick={() => onToggleFavorite(p.name)}
                    aria-label={`${p.name} favorilere ekle`}
                    className="p-2 text-muted-foreground hover:text-primary"
                  >
                    <Star className={`h-4 w-4 ${favorites.includes(p.name) ? "fill-accent text-accent" : ""}`} />
                  </button>
                )}
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-sm text-muted-foreground" data-testid={`${testid}-noresult`}>
                Aramanıza uyan il yok.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Tek bir kaynak gözlemi. Kaynaklar birleştirilmez; her kart kendi metodolojisini taşır. */
export function PriceCard({ row, compact = false }) {
  const price = row.price;
  const dateLabel = row.transaction_date || row.published_at || row.effective_date;
  return (
    <article
      data-testid={`market-price-card-${row.id}`}
      className="flex h-full flex-col rounded-lg border border-border bg-card p-6"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-primary">{row.source_product_name}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {row.product_label}
            {row.quality_class ? ` · ${row.quality_class}` : ""}
            {row.carcass_state === "HOT_CARCASS" ? " · Sıcak karkas" : ""}
            {row.carcass_state === "COLD_CARCASS" ? " · Soğuk karkas" : ""}
          </p>
        </div>
        <SourceBadge tier={row.source_tier} badge={row.source_badge} testid={`market-badge-${row.id}`} />
      </div>

      <p className="mt-4 text-2xl font-extrabold text-primary">
        {nf(price)}
        <span className="ml-1 text-sm font-semibold text-muted-foreground">{row.unit}</span>
      </p>

      {(row.min_price != null || row.max_price != null) && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid={`market-range-${row.id}`}>
          En az {nf(row.min_price)} · En çok {nf(row.max_price)} · Ortalama {nf(row.average_price)}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {row.status === "UPCOMING" && (
          <span data-testid={`market-upcoming-${row.id}`} className="rounded-full bg-secondary/15 px-2.5 py-0.5 text-[11px] font-bold text-secondary">
            Yürürlüğe girecek {dayTR(row.effective_date)}
          </span>
        )}
        {row.stale && (
          <span data-testid={`market-stale-${row.id}`} className="inline-flex items-center gap-1 rounded-full bg-accent/20 px-2.5 py-0.5 text-[11px] font-bold text-primary">
            <AlertTriangle className="h-3 w-3" /> Eski veri
          </span>
        )}
        <span className="rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground">
          {row.price_nature}
        </span>
      </div>

      {!compact && (
        <dl className="mt-3 space-y-1 text-xs text-muted-foreground">
          <div>Kaynak: {row.source_name}{row.exchange_name ? ` — ${row.exchange_name}` : ""}</div>
          <div>
            Kaynak tarihi: {dateLabel ? dayTR(dateLabel) : "kaynakta belirtilmemiş"}
          </div>
          <div>Ciftora son kontrol: {minuteTR(row.last_checked_at) || "-"}</div>
          {row.source_date_note && (
            <div data-testid={`price-source-date-note-${row.id}`}>{row.source_date_note}</div>
          )}
          <div>Güncelleme sıklığı: {FREQ_LABEL[row.update_frequency] || "-"}</div>
          {row.transaction_quantity != null && (
            <div>İşlem miktarı: {nf(row.transaction_quantity, 0)} · Adet: {nf(row.transaction_count, 0)}</div>
          )}
          {row.admin_note && (
            <div data-testid={`market-note-${row.id}`} className="text-primary">
              Ciftora Manuel Notu: {row.admin_note}
            </div>
          )}
        </dl>
      )}

      <p className="mt-3 text-[11px] text-muted-foreground">{row.methodology}</p>

      {row.source_url && (
        <a
          href={row.source_url}
          target="_blank"
          rel="noreferrer"
          data-testid={`market-source-link-${row.id}`}
          className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary underline"
        >
          Kaynağı Gör <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </article>
  );
}

export const CATEGORY_TABS = [
  { id: "", label: "Tümü" },
  { id: "BUYUKBAS", label: "Büyükbaş" },
  { id: "KUCUKBAS", label: "Küçükbaş" },
  { id: "SUT", label: "Süt" },
  { id: "KANATLI", label: "Kanatlı" },
  { id: "YEM", label: "Yem" },
];

export const Tabs = ({ value, onChange, items, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {items.map((t) => (
      <button
        key={t.id || "all"}
        type="button"
        data-testid={`${testid}-${t.id || "all"}`}
        onClick={() => onChange(t.id)}
        className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
          value === t.id ? "bg-primary text-primary-foreground" : "border border-border bg-card text-primary hover:bg-muted"
        }`}
      >
        {value === t.id && <Check className="mr-1 inline h-3.5 w-3.5" />}
        {t.label}
      </button>
    ))}
  </div>
);
